/**
 * External JS library runtime loader (ADR-0046, Slice 3a).
 *
 * Loads an app's pinned external libraries (`apps.js_libraries`) from their CDN
 * URL at runtime and returns a `{ globalName -> value }` map, with each value
 * marked a TRUSTED LIBRARY VALUE (`asTrustedLibValue`) so it becomes callable
 * from `{{ }}` bindings (the trusted-namespace exception, see `pageExpr.ts`) AND
 * usable inside JS Objects / `run_js`. Delivery is runtime-CDN: a UMD bundle is
 * injected as a `<script src>` and read off `window[global]`; an ESM bundle is
 * dynamically `import()`ed.
 *
 * Loading is async + cached per stable library-set signature (so the same set
 * loads once per session, shared by every page of the app). A library that
 * fails to load is SKIPPED — its global is simply absent, so a binding /
 * JS Object that references it gets `undefined` rather than crashing the page
 * (a missing library must not take down the whole portal).
 */

import { useEffect, useMemo, useState } from "react";

import { asTrustedLibValue } from "./pageExpr";
import type { JsLibraryOut } from "./api-apps";

/** globalName → the loaded (trusted) library value. */
export type LibraryMap = Record<string, unknown>;

/**
 * The injection surface for the JS-Object host (`compileJsObject`): the library
 * globals (passed as `new Function` params so a JS Object can reference `_`,
 * `dayjs`, …) PLUS a `require(name)` shim resolving a library by its PACKAGE
 * name (`require('lodash')`). A STABLE identity per loaded set (used as the
 * compile cache key), so build it once via {@link buildLibraryContext}.
 */
export interface LibraryContext {
  globals: LibraryMap;
  require: (name: string) => unknown;
}

/** Build a stable {@link LibraryContext} from a loaded map + the library list
 *  (for the package-name → value `require` lookup). */
export function buildLibraryContext(
  map: LibraryMap,
  libs: JsLibraryOut[] | undefined,
): LibraryContext {
  const byName: LibraryMap = {};
  for (const l of libs ?? []) {
    if (l && l.name && map[l.global] !== undefined) byName[l.name] = map[l.global];
  }
  return { globals: map, require: (name: string) => byName[name] };
}

// Cache keyed by a stable signature of the library set: same set → one load.
const _cache = new Map<string, Promise<LibraryMap>>();

function signature(libs: JsLibraryOut[]): string {
  return libs
    .map((l) => `${l.format}:${l.global}:${l.url}`)
    .sort()
    .join("|");
}

function cssEscape(s: string): string {
  // CSS.escape is absent in jsdom; the fallback only needs to be selector-safe.
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, "\\$&");
}

/** Load a UMD bundle via a `<script src>` and read `window[global]`. Reuses an
 *  already-injected tag (idempotent across pages of the same app). */
function loadUmd(url: string, global: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>;
    const sel = `script[data-tray-lib="${cssEscape(url)}"]`;
    const existing = document.querySelector(sel) as HTMLScriptElement | null;
    const done = () => resolve(w[global]);
    if (existing) {
      if (existing.dataset.trayLoaded === "1") return done();
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", () => reject(new Error(`js_library load failed: ${url}`)), {
        once: true,
      });
      return;
    }
    const s = document.createElement("script");
    s.src = url;
    s.async = false;
    s.dataset.trayLib = url;
    s.addEventListener(
      "load",
      () => {
        s.dataset.trayLoaded = "1";
        done();
      },
      { once: true },
    );
    s.addEventListener("error", () => reject(new Error(`js_library load failed: ${url}`)), {
      once: true,
    });
    document.head.appendChild(s);
  });
}

/** Load an ESM bundle via dynamic `import()`. The value is the default export
 *  (or the whole namespace when there's no default). */
async function loadEsm(url: string): Promise<unknown> {
  const mod = (await import(/* @vite-ignore */ url)) as Record<string, unknown>;
  return mod.default ?? mod;
}

/** Indirection so tests (jsdom can't `<script src>`/`import()`) can stub the
 *  actual network load. Production routes umd→`<script>`, esm→`import()`. */
export const _loaders = {
  loadOne: (lib: JsLibraryOut): Promise<unknown> =>
    lib.format === "esm" ? loadEsm(lib.url) : loadUmd(lib.url, lib.global),
};

/** Load the app's libraries → a trusted `{ global -> value }` map (cached per
 *  set). A failed library is skipped (absent from the map). */
export function loadAppLibraries(libs: JsLibraryOut[] | undefined): Promise<LibraryMap> {
  const list = (libs ?? []).filter((l) => l && l.global && l.url);
  if (list.length === 0) return Promise.resolve({});
  const key = signature(list);
  let p = _cache.get(key);
  if (!p) {
    p = (async () => {
      const out: LibraryMap = {};
      await Promise.all(
        list.map(async (lib) => {
          try {
            const val = await _loaders.loadOne(lib);
            if (val != null) out[lib.global] = asTrustedLibValue(val);
          } catch {
            // degrade: leave this global absent (binding/JS Object → undefined).
          }
        }),
      );
      return out;
    })();
    _cache.set(key, p);
  }
  return p;
}

/** Test seam — drop the per-session load cache. */
export function _clearLibraryCache(): void {
  _cache.clear();
}

/**
 * React hook: load an app's libraries and return a STABLE {@link LibraryContext}
 * (or `undefined` until loaded / when there are none). The renderer threads this
 * into `buildScope` (binding globals) + `compileJsObjectsCached` (JS-Object
 * injection). `undefined` on first render → bindings that reference a library
 * resolve to `undefined` (graceful); the component re-renders once loaded and
 * they light up. Cached per set, so navigation/re-mounts don't reload.
 */
export function useAppLibraries(libs: JsLibraryOut[] | undefined): LibraryContext | undefined {
  const list = useMemo(() => (libs ?? []).filter((l) => l && l.global && l.url), [libs]);
  const sig = useMemo(() => signature(list), [list]);
  const [map, setMap] = useState<LibraryMap | null>(null);
  useEffect(() => {
    if (list.length === 0) {
      setMap(null);
      return;
    }
    let alive = true;
    void loadAppLibraries(list).then((m) => {
      if (alive) setMap(m);
    });
    return () => {
      alive = false;
    };
    // keyed on the stable signature (not the array identity, which changes per
    // fetch); `list` with the same `sig` loads the same cached set.
  }, [sig, list]);
  return useMemo(() => (map ? buildLibraryContext(map, list) : undefined), [map, list]);
}
